import { useEffect, useState, useRef } from "react";
import { api, API, ROLE_LABEL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, BarChart3, Send, X, Check, ClipboardList, Eye, EyeOff, Lock, ShieldCheck, Save, RotateCcw } from "lucide-react";

const CREATOR_ROLES = ["chief","deputy_chief","head_councillor","councillor","executive_director","staff_member","band_registry_clerk"];
const LEADERSHIP_ROLES = ["chief","deputy_chief","head_councillor","councillor","executive_director"];
const CATEGORIES = [
  ["governance","Governance","#89b4fa"],
  ["housing","Housing","#d4b876"],
  ["health","Health","#ef5b6b"],
  ["education","Education","#89b4fa"],
  ["lands","Lands & Resources","#8C8C8C"],
  ["culture","Culture & Language","#d4b876"],
  ["economic","Economic Dev.","#89b4fa"],
  ["general","General","#FFFFFF"],
];
const QUESTION_TYPES = [
  ["single","Single choice"],
  ["multiple","Multiple choice"],
  ["yes_no","Yes / No"],
  ["rating","Rating scale"],
  ["short_text","Short text"],
  ["long_text","Long text"],
];

export default function Surveys() {
  const { user } = useAuth();
  const [surveys, setSurveys] = useState([]);
  const [tab, setTab] = useState("active");
  const [editing, setEditing] = useState(null); // survey object or "new"
  const [responding, setResponding] = useState(null); // survey object
  const [viewingResults, setViewingResults] = useState(null); // survey object
  const canCreate = CREATOR_ROLES.includes(user?.role) || user?.is_super_admin;
  const confirm = useConfirm();

  const load = () => api.get("/surveys").then(r => setSurveys(r.data));
  useEffect(() => { load(); }, []);

  const bucket = { draft: [], active: [], closed: [] };
  surveys.forEach(s => bucket[s.status]?.push(s));

  const del = async (id) => {
    const ok = await confirm({ title: "Delete survey?", destructive: true, message: "This removes the survey and all responses.", confirmLabel: "Delete" });
    if (!ok) return;
    try { await api.delete(`/surveys/${id}`); toast.success("Survey deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const publish = async (id) => {
    try { await api.post(`/surveys/${id}/publish`); toast.success("Survey published — accepting responses"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
  };
  const close = async (id) => {
    const ok = await confirm({ title: "Close survey?", message: "Once closed, no more responses can be submitted.", confirmLabel: "Close survey" });
    if (!ok) return;
    try { await api.post(`/surveys/${id}/close`); toast.success("Survey closed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="flex justify-between items-baseline flex-wrap gap-3">
        <div>
          <div className="label-mono text-[#d4b876] flex items-center gap-2"><ClipboardList size={12}/> OFFICIAL SURVEYS</div>
          <h1 className="font-display text-4xl mt-2">Community consultation instruments</h1>
          <p className="text-white/60 mt-2 max-w-2xl">
            Multi-question surveys authored by Chief, Council, program leads, and clerk. One response per verified member. OCAP-signed. Draft → active → closed lifecycle.
          </p>
        </div>
        {canCreate && (
          <button onClick={()=>setEditing("new")} className="btn-cta primary" data-testid="new-survey-btn"><Plus size={12}/> Create survey</button>
        )}
      </div>

      <div className="flex gap-1 mt-8 border-b border-white/10">
        {[["active",`Active (${bucket.active.length})`, "#89b4fa"], ["draft",`Drafts (${bucket.draft.length})`, "#d4b876"], ["closed",`Closed (${bucket.closed.length})`, "#8C8C8C"]].map(([k, l, c]) => (
          <button key={k} onClick={()=>setTab(k)}
            className={`px-4 py-2 font-mono text-[11px] uppercase tracking-widest border-b-2`}
            style={tab===k ? {borderColor:c, color:c} : {borderColor:"transparent", color:"rgba(255,255,255,0.5)"}}
            data-testid={`survey-tab-${k}`}>{l}</button>
        ))}
      </div>

      <div className="mt-8 space-y-4">
        {bucket[tab].length === 0 && (
          <div className="border border-dashed border-white/15 p-10 text-center">
            <ClipboardList size={22} className="mx-auto text-white/30"/>
            <div className="font-mono text-xs text-white/40 uppercase tracking-widest mt-3">
              {tab === "draft" ? "No drafts. Councillors — start one." : tab === "active" ? "No active surveys." : "No closed surveys yet."}
            </div>
            {canCreate && tab === "draft" && <button onClick={()=>setEditing("new")} className="btn-cta mt-4 mx-auto" data-testid="empty-new-survey">Create first survey</button>}
          </div>
        )}
        {bucket[tab].map(s => (
          <SurveyCard key={s.id} s={s} user={user}
            onEdit={()=>setEditing(s)}
            onDelete={()=>del(s.id)}
            onPublish={()=>publish(s.id)}
            onClose={()=>close(s.id)}
            onRespond={()=>setResponding(s)}
            onResults={()=>setViewingResults(s)}
          />
        ))}
      </div>

      {editing && <SurveyEditor survey={editing === "new" ? null : editing} onClose={()=>setEditing(null)} onSaved={load} />}
      {responding && <SurveyResponder survey={responding} onClose={()=>setResponding(null)} onSubmitted={load} />}
      {viewingResults && <SurveyResults surveyId={viewingResults.id} onClose={()=>setViewingResults(null)} />}
    </div>
  );
}

function SurveyCard({ s, user, onEdit, onDelete, onPublish, onClose, onRespond, onResults }) {
  const cat = CATEGORIES.find(c => c[0] === s.category);
  const statusColor = s.status === "active" ? "#89b4fa" : s.status === "draft" ? "#d4b876" : "#8C8C8C";
  const isCreator = s.created_by === user.id;
  const isLeadership = LEADERSHIP_ROLES.includes(user.role) || user.is_super_admin;
  const canManage = isCreator || user.is_super_admin;
  const closesAt = s.closes_at ? new Date(s.closes_at) : null;
  const targetPct = s.response_target ? Math.round((s.response_count / s.response_target) * 100) : null;

  return (
    <div className="panel p-5" data-testid={`survey-${s.id}`}>
      <div className="flex justify-between items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="tag" style={{color: statusColor}}>{s.status}</span>
            {cat && <span className="tag" style={{color: cat[2]}}>{cat[1]}</span>}
            {s.is_anonymous ? (
              <span className="font-mono text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1"><EyeOff size={10}/> Anonymous</span>
            ) : (
              <span className="font-mono text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1"><Eye size={10}/> Attributed</span>
            )}
            {s.publish_public_results && s.status === "closed" && (
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#89b4fa]">Public results</span>
            )}
          </div>
          <div className="font-display text-xl mt-2">{s.title}</div>
          {s.description && <div className="text-white/70 text-sm mt-2 max-w-3xl">{s.description}</div>}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 font-mono text-xs">
            <div><div className="label-mono">Questions</div>{s.questions.length}</div>
            <div><div className="label-mono">Responses</div>{s.response_count}{s.response_target ? ` / ${s.response_target}` : ""}</div>
            <div><div className="label-mono">Closes</div>{closesAt ? closesAt.toLocaleDateString() : "Open"}</div>
            <div><div className="label-mono">By</div>{s.created_by_name}</div>
          </div>
          {targetPct !== null && (
            <div className="mt-3">
              <div className="flex justify-between font-mono text-[10px] text-white/50 mb-1"><span>RESPONSE TARGET</span><span>{Math.min(targetPct,100)}%</span></div>
              <div className="h-1 bg-white/5"><div className="h-full bg-[#d4b876]" style={{width: `${Math.min(targetPct,100)}%`}}/></div>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          {s.status === "active" && !s.my_responded && (
            <button onClick={onRespond} className="btn-cta primary" data-testid={`respond-${s.id}`}><Send size={12}/> Respond</button>
          )}
          {s.status === "active" && s.my_responded && (
            <span className="font-mono text-[10px] text-[#89b4fa] uppercase tracking-widest text-right flex items-center justify-end gap-1"><Check size={10}/> Responded</span>
          )}
          {(isLeadership || isCreator) && s.status !== "draft" && (
            <button onClick={onResults} className="btn-cta text-xs" data-testid={`results-${s.id}`}><BarChart3 size={12}/> Results</button>
          )}
          {canManage && s.status === "draft" && (
            <>
              <button onClick={onEdit} className="btn-cta text-xs" data-testid={`edit-${s.id}`}><Edit3 size={12}/> Edit</button>
              <button onClick={onPublish} className="btn-cta primary text-xs" data-testid={`publish-${s.id}`}>Publish →</button>
            </>
          )}
          {canManage && s.status === "active" && (
            <button onClick={onClose} className="btn-cta danger text-xs" data-testid={`close-${s.id}`}><X size={12}/> Close</button>
          )}
          {canManage && (
            <button onClick={onDelete} className="text-[#ef5b6b] hover:text-white self-end mt-1" data-testid={`del-${s.id}`}><Trash2 size={14}/></button>
          )}
        </div>
      </div>
      <div className="font-mono text-[10px] text-white/30 mt-4 border-t border-white/10 pt-3 flex items-center gap-2">
        <ShieldCheck size={10}/> OCAP-Signed · {ROLE_LABEL[s.created_by_role]} · {new Date(s.created_at).toLocaleDateString()}
      </div>
    </div>
  );
}

// ==================== EDITOR ====================
function newQuestion() {
  return { id: crypto.randomUUID(), type: "single", prompt: "", required: true, options: ["", ""], scale_min: 1, scale_max: 5 };
}

function SurveyEditor({ survey, onClose, onSaved }) {
  const isEdit = !!survey;
  // localStorage draft key — one per user "new" slot, one per editing survey
  const draftKey = isEdit ? `klfn.survey.draft.edit.${survey.id}` : "klfn.survey.draft.new";

  const [f, setF] = useState(survey || {
    title: "", description: "", category: "general",
    is_anonymous: false, publish_public_results: false,
    closes_at: "", response_target: "",
    questions: [newQuestion()],
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [autosaveStatus, setAutosaveStatus] = useState("idle"); // idle | saving | saved | restored
  const [restorable, setRestorable] = useState(null);
  const autosaveTimer = useRef(null);
  const initialisedRef = useRef(false);

  // Hydrate any prior autosaved draft on open (only for the "new" slot — edits already have server truth)
  useEffect(() => {
    if (isEdit) { initialisedRef.current = true; return; }
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) { initialisedRef.current = true; return; }
      const stored = JSON.parse(raw);
      // Only surface restore prompt if the draft actually has meaningful content
      const hasContent = stored?.data?.title?.trim() || (stored?.data?.questions || []).some(q => q?.prompt?.trim());
      if (hasContent) {
        setRestorable(stored);
      } else {
        localStorage.removeItem(draftKey);
      }
    } catch (_) { /* ignore corrupt */ }
    initialisedRef.current = true;
  }, [draftKey, isEdit]);

  // Debounced localStorage autosave — fires 1.5s after last edit
  useEffect(() => {
    if (!initialisedRef.current) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    setAutosaveStatus("saving");
    autosaveTimer.current = setTimeout(() => {
      try {
        localStorage.setItem(draftKey, JSON.stringify({ data: f, saved_at: Date.now() }));
        setAutosaveStatus("saved");
      } catch (_) { setAutosaveStatus("idle"); }
    }, 1500);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
  }, [f, draftKey]);

  const restoreDraft = () => {
    setF(restorable.data);
    setRestorable(null);
    setAutosaveStatus("restored");
    toast.success("Draft restored from your last session");
  };
  const dismissDraft = () => {
    localStorage.removeItem(draftKey);
    setRestorable(null);
  };

  const updateQ = (idx, patch) => {
    const questions = [...f.questions];
    questions[idx] = { ...questions[idx], ...patch };
    setF({ ...f, questions });
  };
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= f.questions.length) return;
    const questions = [...f.questions];
    [questions[idx], questions[j]] = [questions[j], questions[idx]];
    setF({ ...f, questions });
  };
  const remove = (idx) => setF({ ...f, questions: f.questions.filter((_, i) => i !== idx) });

  const save = async (e) => {
    e.preventDefault(); setSaving(true); setErr("");
    try {
      const payload = {
        title: f.title, description: f.description || null, category: f.category,
        is_anonymous: !!f.is_anonymous, publish_public_results: !!f.publish_public_results,
        closes_at: f.closes_at ? new Date(f.closes_at).toISOString() : null,
        response_target: f.response_target ? Number(f.response_target) : null,
        questions: f.questions,
      };
      if (isEdit) await api.patch(`/surveys/${survey.id}`, payload);
      else await api.post("/surveys", payload);
      // Successful save — clear the autosaved cache
      localStorage.removeItem(draftKey);
      toast.success(isEdit ? "Draft updated" : "Draft saved");
      onSaved(); onClose();
    } catch (e) { setErr(e.response?.data?.detail || "Failed"); toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 overflow-y-auto p-4" onClick={onClose}>
      <form onClick={e=>e.stopPropagation()} onSubmit={save} className="max-w-3xl mx-auto border border-white/20 bg-[#181825] p-6 space-y-5 my-8" data-testid="survey-editor">
        <div className="flex justify-between items-baseline">
          <div className="label-mono text-[#d4b876]">::: {isEdit?"EDIT":"NEW"} SURVEY DRAFT</div>
          <div className="flex items-center gap-4">
            <AutosaveIndicator status={autosaveStatus} />
            <span className="font-mono text-[10px] text-white/40 flex items-center gap-1"><Lock size={10}/> Draft — only you can see it</span>
          </div>
        </div>

        {restorable && (
          <div className="border border-[#d4b876]/40 bg-[#d4b876]/5 p-3 flex items-start justify-between gap-3" data-testid="restore-draft-banner">
            <div className="flex-1">
              <div className="label-mono text-[#d4b876] mb-1">::: Draft recovered</div>
              <div className="font-mono text-xs text-white/70">
                We saved your last session automatically. Restore your work-in-progress or start fresh.
                {restorable.saved_at && <span className="text-white/40"> · saved {new Date(restorable.saved_at).toLocaleString()}</span>}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button type="button" onClick={restoreDraft} className="btn-cta primary text-xs" data-testid="restore-draft-btn">
                <RotateCcw size={11}/> Restore
              </button>
              <button type="button" onClick={dismissDraft} className="btn-cta text-xs" data-testid="dismiss-draft-btn">Discard</button>
            </div>
          </div>
        )}

        <input required placeholder="Survey title" value={f.title} onChange={e=>setF({...f, title:e.target.value})} className="w-full px-3 py-2.5 font-mono text-sm" data-testid="survey-title"/>
        <textarea rows={3} placeholder="Description / purpose (visible to respondents)" value={f.description || ""} onChange={e=>setF({...f, description:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="survey-description"/>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-mono block mb-1">Category</label>
            <select value={f.category} onChange={e=>setF({...f, category:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="survey-category">
              {CATEGORIES.map(([k,l])=><option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="label-mono block mb-1">Closes at (optional)</label>
            <input type="datetime-local" value={f.closes_at || ""} onChange={e=>setF({...f, closes_at:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="survey-closes"/>
          </div>
          <div>
            <label className="label-mono block mb-1">Response target (auto-close, optional)</label>
            <input type="number" min="1" value={f.response_target || ""} onChange={e=>setF({...f, response_target:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="survey-target"/>
          </div>
          <div className="space-y-2 pt-6">
            <label className="flex items-center gap-2 font-mono text-xs">
              <input type="checkbox" checked={!!f.is_anonymous} onChange={e=>setF({...f, is_anonymous:e.target.checked})} data-testid="survey-anonymous"/>
              Anonymous responses (strip identity)
            </label>
            <label className="flex items-center gap-2 font-mono text-xs">
              <input type="checkbox" checked={!!f.publish_public_results} onChange={e=>setF({...f, publish_public_results:e.target.checked})} data-testid="survey-public-results"/>
              Publish aggregate results publicly on close
            </label>
          </div>
        </div>

        <div className="divider-line"/>

        <div>
          <div className="label-mono text-[#89b4fa] mb-3">QUESTIONS ({f.questions.length})</div>
          <div className="space-y-4">
            {f.questions.map((q, i) => (
              <div key={q.id} className="border border-white/10 p-4 relative" data-testid={`question-${i}`}>
                <div className="flex justify-between items-center mb-3">
                  <span className="font-mono text-[10px] text-white/50 uppercase tracking-widest">Q{i+1}</span>
                  <div className="flex gap-1">
                    <button type="button" onClick={()=>move(i,-1)} disabled={i===0} className="font-mono text-xs text-white/60 hover:text-white disabled:opacity-30 px-1" data-testid={`q-up-${i}`}>↑</button>
                    <button type="button" onClick={()=>move(i,1)} disabled={i===f.questions.length-1} className="font-mono text-xs text-white/60 hover:text-white disabled:opacity-30 px-1" data-testid={`q-down-${i}`}>↓</button>
                    <button type="button" onClick={()=>remove(i)} disabled={f.questions.length===1} className="text-[#ef5b6b] hover:text-white disabled:opacity-30" data-testid={`q-del-${i}`}><Trash2 size={12}/></button>
                  </div>
                </div>
                <div className="grid sm:grid-cols-[1fr_180px] gap-3">
                  <input required placeholder="Question prompt" value={q.prompt} onChange={e=>updateQ(i,{prompt:e.target.value})} className="px-3 py-2 font-mono text-sm" data-testid={`q-prompt-${i}`}/>
                  <select value={q.type} onChange={e=>updateQ(i,{type:e.target.value})} className="px-3 py-2 font-mono text-sm" data-testid={`q-type-${i}`}>
                    {QUESTION_TYPES.map(([k,l])=><option key={k} value={k}>{l}</option>)}
                  </select>
                </div>
                {(q.type === "single" || q.type === "multiple") && (
                  <div className="mt-3 space-y-2">
                    {q.options.map((opt, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <span className="font-mono text-[10px] text-white/40 w-4">{oi+1}.</span>
                        <input placeholder={`Option ${oi+1}`} value={opt} onChange={e=>{ const opts=[...q.options]; opts[oi]=e.target.value; updateQ(i,{options:opts}); }} className="flex-1 px-3 py-1.5 font-mono text-xs" data-testid={`q-${i}-opt-${oi}`}/>
                        <button type="button" onClick={()=>updateQ(i,{options: q.options.filter((_,k)=>k!==oi)})} disabled={q.options.length<=2} className="text-white/40 hover:text-[#ef5b6b] disabled:opacity-30 px-1"><X size={12}/></button>
                      </div>
                    ))}
                    <button type="button" onClick={()=>updateQ(i,{options:[...q.options, ""]})} className="font-mono text-[10px] uppercase tracking-widest text-[#89b4fa] hover:underline" data-testid={`q-add-opt-${i}`}>+ Add option</button>
                  </div>
                )}
                {q.type === "rating" && (
                  <div className="mt-3 flex items-center gap-3">
                    <label className="font-mono text-xs text-white/60">Scale</label>
                    <input type="number" min="0" max="10" value={q.scale_min} onChange={e=>updateQ(i,{scale_min:Number(e.target.value)})} className="w-16 px-2 py-1 font-mono text-xs"/>
                    <span className="text-white/40">to</span>
                    <input type="number" min="1" max="10" value={q.scale_max} onChange={e=>updateQ(i,{scale_max:Number(e.target.value)})} className="w-16 px-2 py-1 font-mono text-xs"/>
                  </div>
                )}
                <label className="mt-3 flex items-center gap-2 font-mono text-[10px] text-white/60">
                  <input type="checkbox" checked={q.required} onChange={e=>updateQ(i,{required:e.target.checked})}/>
                  Required
                </label>
              </div>
            ))}
          </div>
          <button type="button" onClick={()=>setF({...f, questions:[...f.questions, newQuestion()]})} className="btn-cta mt-4" data-testid="add-question-btn"><Plus size={12}/> Add question</button>
        </div>

        {err && <div className="text-[#ef5b6b] font-mono text-xs">{err}</div>}
        <div className="flex justify-end gap-2 pt-4 border-t border-white/10">
          <button type="button" onClick={onClose} className="btn-cta">Cancel</button>
          <button type="submit" disabled={saving} className="btn-cta primary" data-testid="save-survey-btn">{saving?"Saving...":"Save draft"}</button>
        </div>
      </form>
    </div>
  );
}

// ==================== RESPONDER ====================
function SurveyResponder({ survey, onClose, onSubmitted }) {
  const [full, setFull] = useState(null);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(0);
  const [autosaveStatus, setAutosaveStatus] = useState("idle"); // idle | saving | saved
  const [draftRestored, setDraftRestored] = useState(false);
  const draftTimer = useRef(null);
  const initialisedRef = useRef(false);

  // Hydrate the full survey + any prior draft in parallel
  useEffect(() => {
    let alive = true;
    (async () => {
      const [surveyR, draftR] = await Promise.all([
        api.get(`/surveys/${survey.id}`),
        api.get(`/surveys/${survey.id}/draft`).catch(() => ({ data: { exists: false } })),
      ]);
      if (!alive) return;
      setFull(surveyR.data);
      if (draftR.data?.exists) {
        setAnswers(draftR.data.answers || {});
        setStep(Math.min(draftR.data.step || 0, (surveyR.data.questions?.length || 1) - 1));
        setDraftRestored(true);
        setAutosaveStatus("saved");
      }
      // Small delay so hydration doesn't count as a "change"
      setTimeout(() => { initialisedRef.current = true; }, 250);
    })();
    return () => { alive = false; };
  }, [survey.id]);

  // Debounced server-side draft save whenever answers or step change
  useEffect(() => {
    if (!initialisedRef.current || !full) return;
    if (draftTimer.current) clearTimeout(draftTimer.current);
    setAutosaveStatus("saving");
    draftTimer.current = setTimeout(async () => {
      try {
        await api.put(`/surveys/${survey.id}/draft`, { answers, step });
        setAutosaveStatus("saved");
      } catch (_) { setAutosaveStatus("idle"); }
    }, 1200);
    return () => { if (draftTimer.current) clearTimeout(draftTimer.current); };
  }, [answers, step, survey.id, full]);

  if (!full) return null;

  const q = full.questions[step];
  const setAns = (v) => setAnswers({ ...answers, [q.id]: v });
  const val = answers[q.id];
  const canProceed = !q.required || (val !== undefined && val !== "" && !(Array.isArray(val) && val.length === 0));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/surveys/${survey.id}/respond`, { answers });
      // Purge draft on successful submit
      api.delete(`/surveys/${survey.id}/draft`).catch(() => {});
      toast.success("Response submitted · Miigwech");
      onSubmitted(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
    finally { setSaving(false); }
  };

  const pct = Math.round(((step + 1) / full.questions.length) * 100);

  return (
    <div className="fixed inset-0 z-50 bg-black/95 overflow-y-auto p-4" onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} className="max-w-2xl mx-auto border border-white/20 bg-[#181825] p-8 my-8" data-testid="survey-responder">
        <div className="flex justify-between items-start gap-4">
          <div>
            <div className="label-mono text-[#d4b876]">::: {full.is_anonymous ? "ANONYMOUS " : ""}RESPONSE</div>
            <h2 className="font-display text-2xl mt-2">{full.title}</h2>
            {full.description && <p className="text-white/60 text-sm mt-3">{full.description}</p>}
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="close-responder"><X size={20}/></button>
            <AutosaveIndicator status={autosaveStatus} />
          </div>
        </div>

        {draftRestored && (
          <div className="mt-4 border border-[#89b4fa]/40 bg-[#89b4fa]/5 p-3 font-mono text-xs text-[#89b4fa] flex items-center gap-2" data-testid="responder-restored-banner">
            <RotateCcw size={12}/> Your in-progress answers were restored from your last session.
          </div>
        )}

        <div className="mt-6 flex items-center gap-3">
          <div className="flex-1 h-1 bg-white/10"><div className="h-full bg-[#89b4fa] transition-all" style={{width:`${pct}%`}}/></div>
          <span className="font-mono text-[10px] text-white/50 tabular-nums">{step+1}/{full.questions.length}</span>
        </div>

        <div className="mt-8 min-h-[240px]">
          <div className="font-mono text-[10px] text-[#89b4fa] uppercase tracking-widest">Question {step+1}{q.required && " · Required"}</div>
          <div className="font-display text-xl mt-2">{q.prompt}</div>
          <div className="mt-6">
            {q.type === "single" && (
              <div className="space-y-2">
                {q.options.map((opt, i) => (
                  <button key={i} type="button" onClick={()=>setAns(opt)}
                    className={`block w-full text-left border px-4 py-3 font-mono text-sm ${val===opt ? "border-[#89b4fa] bg-[#89b4fa]/10 text-white" : "border-white/15 text-white/70 hover:border-white/40"}`}
                    data-testid={`answer-single-${i}`}>
                    {val===opt && <Check size={12} className="inline text-[#89b4fa] mr-2"/>}{opt}
                  </button>
                ))}
              </div>
            )}
            {q.type === "multiple" && (
              <div className="space-y-2">
                {q.options.map((opt, i) => {
                  const arr = val || [];
                  const on = arr.includes(opt);
                  return (
                    <button key={i} type="button" onClick={()=>setAns(on ? arr.filter(x=>x!==opt) : [...arr, opt])}
                      className={`block w-full text-left border px-4 py-3 font-mono text-sm ${on ? "border-[#89b4fa] bg-[#89b4fa]/10 text-white" : "border-white/15 text-white/70 hover:border-white/40"}`}
                      data-testid={`answer-multi-${i}`}>
                      <span className="inline-block w-3 h-3 border border-current mr-3 align-middle">{on && <Check size={10}/>}</span>{opt}
                    </button>
                  );
                })}
              </div>
            )}
            {q.type === "yes_no" && (
              <div className="grid grid-cols-2 gap-3">
                {[["yes","YES","#89b4fa"],["no","NO","#ef5b6b"]].map(([v,l,c])=>(
                  <button key={v} type="button" onClick={()=>setAns(v)}
                    className={`border px-4 py-6 font-display text-2xl`}
                    style={val===v ? {borderColor:c, background: `${c}22`, color:c} : {borderColor:"rgba(255,255,255,0.15)", color:"rgba(255,255,255,0.7)"}}
                    data-testid={`answer-yn-${v}`}>{l}</button>
                ))}
              </div>
            )}
            {q.type === "rating" && (
              <div>
                <div className="flex gap-2 flex-wrap">
                  {Array.from({length: q.scale_max - q.scale_min + 1}, (_, i) => q.scale_min + i).map(n => (
                    <button key={n} type="button" onClick={()=>setAns(n)}
                      className={`w-14 h-14 border font-display text-xl ${val===n ? "border-[#89b4fa] bg-[#89b4fa]/20 text-[#89b4fa]" : "border-white/15 text-white/70 hover:border-white/40"}`}
                      data-testid={`answer-rating-${n}`}>{n}</button>
                  ))}
                </div>
                <div className="mt-3 font-mono text-[10px] text-white/40 tracking-widest uppercase flex justify-between">
                  <span>Low ({q.scale_min})</span><span>High ({q.scale_max})</span>
                </div>
              </div>
            )}
            {q.type === "short_text" && (
              <input placeholder="Your answer" value={val || ""} onChange={e=>setAns(e.target.value)} className="w-full px-3 py-2.5 font-mono text-sm" data-testid="answer-short"/>
            )}
            {q.type === "long_text" && (
              <textarea rows={5} placeholder="Your answer" value={val || ""} onChange={e=>setAns(e.target.value)} className="w-full px-3 py-2 font-mono text-sm" data-testid="answer-long"/>
            )}
          </div>
        </div>

        <div className="mt-8 flex justify-between items-center pt-4 border-t border-white/10">
          <button type="button" onClick={()=>setStep(step-1)} disabled={step===0} className="btn-cta text-xs disabled:opacity-30" data-testid="prev-question">← Previous</button>
          <div className="font-mono text-[10px] text-white/50 flex items-center gap-2"><ShieldCheck size={10} className="text-[#89b4fa]"/> {full.is_anonymous ? "Anonymous · identity stripped at submit" : "Attributed · signed with your registry number"}</div>
          {step < full.questions.length - 1 ? (
            <button type="button" onClick={()=>setStep(step+1)} disabled={!canProceed} className="btn-cta primary text-xs disabled:opacity-30" data-testid="next-question">Next →</button>
          ) : (
            <button type="button" onClick={submit} disabled={!canProceed || saving} className="btn-cta primary text-xs" data-testid="submit-response">{saving?"Submitting...":"Submit response"}</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== RESULTS ====================
function SurveyResults({ surveyId, onClose }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/surveys/${surveyId}/results`).then(r=>setData(r.data)).catch(()=>{}); }, [surveyId]);
  if (!data) return null;

  const canExport = LEADERSHIP_ROLES.includes(user.role) || user.is_super_admin;
  // Direct anchor URL — same-origin cookies are sent by the browser natively,
  // giving a real download event (visible in Playwright + preserves the server-side filename).
  const exportHref = `${API}/surveys/${surveyId}/export.csv`;
  const onExportClick = () => {
    if (data.total_responses === 0) return;
    toast.success(`Exporting ${data.total_responses} response${data.total_responses===1?"":"s"}…`);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/95 overflow-y-auto p-4" onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} className="max-w-3xl mx-auto border border-white/20 bg-[#181825] p-8 my-8" data-testid="survey-results">
        <div className="flex justify-between items-start gap-4 flex-wrap">
          <div>
            <div className="label-mono text-[#89b4fa]">LIVE RESULTS</div>
            <h2 className="font-display text-3xl mt-2">{data.title}</h2>
            <div className="font-mono text-xs text-white/50 mt-2">
              {data.total_responses} responses · {data.status.toUpperCase()}
              {data.response_target && ` · target ${data.response_target}`}
              {data.is_anonymous && " · anonymous"}
            </div>
          </div>
          <div className="flex gap-2">
            {canExport && (
              <a
                href={exportHref}
                onClick={onExportClick}
                download
                className={`btn-cta gold text-xs ${data.total_responses === 0 ? "opacity-40 pointer-events-none" : ""}`}
                data-testid="export-csv-btn"
                title={data.total_responses === 0 ? "No responses to export" : "Download raw response data as CSV"}
                aria-disabled={data.total_responses === 0}
              >
                ↓ Export CSV
              </a>
            )}
            <button onClick={onClose} className="btn-cta">Close</button>
          </div>
        </div>

        <div className="mt-8 space-y-6">
          {data.questions.map((q, i) => (
            <div key={q.question_id} className="border border-white/10 p-5">
              <div className="font-mono text-[10px] text-[#d4b876] uppercase tracking-widest">Q{i+1} · {q.type.replace("_"," ")} · {q.answered} answered</div>
              <div className="font-display text-lg mt-2">{q.prompt}</div>
              <div className="mt-4">
                {(q.type === "single" || q.type === "yes_no" || q.type === "multiple") && (
                  <BarBreakdown counts={q.counts || {}} total={q.type === "multiple" ? Object.values(q.counts||{}).reduce((a,b)=>a+b,0) : q.answered} />
                )}
                {q.type === "rating" && (
                  <div>
                    <div className="font-display text-4xl text-[#89b4fa]">{q.average ?? "—"}<span className="text-white/40 text-lg"> avg</span></div>
                    <div className="mt-3"><BarBreakdown counts={q.distribution || {}} total={q.answered}/></div>
                  </div>
                )}
                {(q.type === "short_text" || q.type === "long_text") && (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {q.responses ? q.responses.map((r, j) => (
                      <div key={j} className="border border-white/10 p-3 text-sm text-white/80 font-mono">"{r}"</div>
                    )) : (
                      <div className="font-mono text-xs text-white/50">{q.responses_available || 0} text response(s) — visible to creator and leadership</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BarBreakdown({ counts, total }) {
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0 || total === 0) return <div className="font-mono text-xs text-white/40">No data yet.</div>;
  return (
    <div className="space-y-2">
      {entries.map(([k, v]) => {
        const pct = total ? Math.round((v / total) * 100) : 0;
        return (
          <div key={k}>
            <div className="flex justify-between font-mono text-xs">
              <span className="text-white/80 truncate mr-2">{k}</span>
              <span className="text-white/60 tabular-nums whitespace-nowrap">{v} · {pct}%</span>
            </div>
            <div className="h-2 bg-white/5 mt-1 border border-white/10">
              <div className="h-full bg-[#89b4fa]" style={{width: `${pct}%`}}/>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AutosaveIndicator({ status }) {
  if (status === "idle") return null;
  const map = {
    saving:   { color: "#d4b876", label: "Saving…",  icon: Save },
    saved:    { color: "#a6e3a1", label: "Auto-saved locally", icon: Check },
    restored: { color: "#89b4fa", label: "Draft restored", icon: RotateCcw },
  };
  const m = map[status];
  if (!m) return null;
  const Icon = m.icon;
  return (
    <span className="font-mono text-[10px] flex items-center gap-1" style={{ color: m.color }} data-testid="autosave-indicator">
      <Icon size={10}/> {m.label}
    </span>
  );
}

